/** Recuperação de senha por auto-serviço.
 *
 *  O backend responde sempre a mesma coisa, exista a conta ou não, e a tela
 *  precisa fazer o mesmo: uma confirmação que revelasse "este e-mail não está
 *  cadastrado" transformaria a página num verificador de contas.
 *
 *  A senha atual de quem pede continua valendo até a temporária ser usada, e a
 *  confirmação diz isso, para ninguém achar que perdeu o acesso ao pedir.
 */
import { useState } from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { MailCheck } from 'lucide-react'
import { authApi } from '../../core/auth/authApi'
import { apiErrorMessage } from '../../shared/apiError'
import { useI18n } from '@/core/i18n/useI18n'
import { AuthLayout } from './AuthLayout'

export function EsqueciSenhaPage() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    if (!email.trim() || ocupado) return
    setOcupado(true)
    setErro(null)
    try {
      await authApi.forgotPassword(email.trim().toLowerCase())
      setEnviado(true)
    } catch (falha) {
      setErro(apiErrorMessage(falha))
    } finally {
      setOcupado(false)
    }
  }

  const voltar = { para: '/login', texto: t('auth.esqueciSenha.backToLogin') }

  if (enviado) {
    return (
      <AuthLayout
        titulo={t('auth.esqueciSenha.sentTitle')}
        subtitulo={t('auth.esqueciSenha.sentSubtitle')}
        voltar={voltar}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
              p: 2,
              borderRadius: 'var(--radius-leaf-sm)',
              backgroundColor: 'var(--color-primary-subtle)',
              color: 'var(--color-text)',
            }}
          >
            <MailCheck size={20} style={{ flexShrink: 0, color: 'var(--color-primary)' }} />
            <Typography variant="body2">{t('auth.esqueciSenha.sentBody')}</Typography>
          </Stack>

          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
            {t('auth.esqueciSenha.currentPasswordKeeps')}
          </Typography>
        </Stack>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      titulo={t('auth.esqueciSenha.title')}
      subtitulo={t('auth.esqueciSenha.subtitle')}
      voltar={voltar}
    >
      <form onSubmit={enviar} noValidate>
        <Stack spacing={2}>
          <TextField
            id="email"
            label={t('auth.esqueciSenha.emailLabel')}
            type="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.loginPage.emailPlaceholder')}
            autoComplete="username"
            autoFocus
            disabled={ocupado}
            fullWidth
          />

          {erro && (
            <div className="login-error" role="alert">
              {erro}
            </div>
          )}

          <Button type="submit" variant="contained" size="large" disabled={!email.trim() || ocupado}>
            {ocupado ? t('auth.esqueciSenha.sending') : t('auth.esqueciSenha.send')}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  )
}
